/**
 * Analytics & Operations (Phase 4 redesign).
 *
 * Renders 4 read-only surfaces powered by /api/analytics/* endpoints:
 *   1. Booking + cancellation trend series (day / week / month bucket)
 *   2. OTA performance table (bookings, room-nights, ADR, share, cancel rate)
 *   3. Integration Health grid (per property × OTA, last status / error / at)
 *   4. Activity Logs (User audit trail vs Integration operational logs)
 *
 * Property scope:
 *   - `propertyId=null` (All Properties mode) → aggregates across the visible
 *     portfolio (backend derives scope from the user's role).
 *   - `propertyId=<uuid>` (single-property mode) → the same endpoints are
 *     invoked with `property_ids=<uuid>` so responses are pre-scoped by the
 *     backend `_resolve_scope` helper.
 *
 * Zero MMT files are touched; the Integration Health surface is a read-only
 * consumer of existing collections (ota_property_mappings, listing_sync_logs,
 * mmt_notifications, mmt_webhook_errors).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DATE_PRESETS = [
  { key: '7',   label: 'Last 7 days',   days: 7 },
  { key: '30',  label: 'Last 30 days',  days: 30 },
  { key: '90',  label: 'Last 90 days',  days: 90 },
  { key: 'mtd', label: 'Month to date' },
];

const BUCKETS = [
  { key: 'day',   label: 'Day' },
  { key: 'week',  label: 'Week' },
  { key: 'month', label: 'Month' },
];

const SUB_TABS = [
  { key: 'trends',      label: 'Booking Trends' },
  { key: 'ota',         label: 'OTA Performance' },
  { key: 'health',      label: 'Integration Health' },
  { key: 'activity',    label: 'Activity Logs' },
];

function isoDaysAgo(days) {
  const d = new Date(); d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
}
function isoMonthStart() {
  const d = new Date(); d.setDate(1);
  return d.toISOString().slice(0, 10);
}
function today() { return new Date().toISOString().slice(0, 10); }

function computeRange(presetKey) {
  if (presetKey === 'mtd') return { from: isoMonthStart(), to: today() };
  const p = DATE_PRESETS.find(x => x.key === presetKey);
  return { from: isoDaysAgo(p ? p.days : 30), to: today() };
}

const currency = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
const percent  = (n) => `${Number(n || 0).toFixed(1)}%`;

const authHeader = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

export function AnalyticsOperations({ propertyId = null, allPropertiesMode = true }) {
  const [subTab, setSubTab]   = useState('trends');
  const [preset, setPreset]   = useState('30');
  const [bucket, setBucket]   = useState('day');
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);
  const [trends, setTrends]   = useState(null);   // portfolio-trends payload
  const [ota, setOta]         = useState(null);   // ota-performance payload
  const [health, setHealth]   = useState(null);   // integration-health payload
  const [logsKind, setLogsKind] = useState('user'); // 'user' | 'integration'
  const [logs, setLogs]       = useState(null);   // activity-logs payload

  const range = useMemo(() => computeRange(preset), [preset]);
  const scopeParam = useMemo(() => {
    if (!allPropertiesMode && propertyId) return `&property_ids=${encodeURIComponent(propertyId)}`;
    return '';
  }, [allPropertiesMode, propertyId]);

  const fetchTrends = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const url = `${API}/analytics/portfolio-trends?from_date=${range.from}&to_date=${range.to}&bucket=${bucket}${scopeParam}`;
      const r = await axios.get(url, authHeader());
      setTrends(r?.data || null);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to load trends');
    } finally {
      setLoading(false);
    }
  }, [range.from, range.to, bucket, scopeParam]);

  const fetchOta = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const url = `${API}/analytics/ota-performance?from_date=${range.from}&to_date=${range.to}${scopeParam}`;
      const r = await axios.get(url, authHeader());
      setOta(r?.data || null);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to load OTA performance');
    } finally {
      setLoading(false);
    }
  }, [range.from, range.to, scopeParam]);

  const fetchHealth = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const url = `${API}/analytics/integration-health?${scopeParam.replace(/^&/, '')}`;
      const r = await axios.get(url, authHeader());
      setHealth(r?.data || null);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to load integration health');
    } finally {
      setLoading(false);
    }
  }, [scopeParam]);

  const fetchLogs = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const url = `${API}/analytics/activity-logs?kind=${logsKind}&limit=100`;
      const r = await axios.get(url, authHeader());
      setLogs(r?.data || null);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to load activity logs');
    } finally {
      setLoading(false);
    }
  }, [logsKind]);

  useEffect(() => {
    if (subTab === 'trends')   fetchTrends();
    if (subTab === 'ota')      fetchOta();
    if (subTab === 'health')   fetchHealth();
    if (subTab === 'activity') fetchLogs();
  }, [subTab, fetchTrends, fetchOta, fetchHealth, fetchLogs]);

  const scopeLabel = allPropertiesMode
    ? 'All Properties'
    : (propertyId ? `Single Property (${propertyId.slice(0, 8)}…)` : 'No selection');

  return (
    <div className="space-y-4" data-testid="analytics-operations">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold text-gray-800">Analytics &amp; Operations</h2>
          <div className="text-xs text-gray-500 mt-0.5">
            Scope: <span className="font-medium text-gray-700">{scopeLabel}</span>
            {' · '}Read-only aggregates. MMT integration is frozen.
          </div>
        </div>
      </div>

      {/* Sub-tab strip */}
      <div className="border-b flex items-end gap-1" data-testid="analytics-subtabs">
        {SUB_TABS.map(t => (
          <button
            key={t.key}
            onClick={() => setSubTab(t.key)}
            data-testid={`analytics-subtab-${t.key}`}
            className={`px-3 py-2 text-sm border-b-2 -mb-px transition-colors ${
              subTab === t.key
                ? 'border-orange-600 text-orange-700 font-semibold'
                : 'border-transparent text-gray-600 hover:text-gray-800'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Filter bar (only for trends / ota which use date range) */}
      {(subTab === 'trends' || subTab === 'ota') && (
        <div className="flex flex-wrap items-end gap-3 p-3 border rounded-lg bg-white" data-testid="analytics-filter-bar">
          <div>
            <div className="text-[11px] text-gray-500 mb-1">Date range</div>
            <select
              value={preset}
              onChange={(e) => setPreset(e.target.value)}
              className="text-sm border rounded px-2 py-1.5 min-w-[140px]"
              data-testid="analytics-date-preset"
            >
              {DATE_PRESETS.map(p => <option key={p.key} value={p.key}>{p.label}</option>)}
            </select>
            <div className="text-[10px] text-gray-400 mt-1">{range.from} → {range.to}</div>
          </div>

          {subTab === 'trends' && (
            <div>
              <div className="text-[11px] text-gray-500 mb-1">Bucket</div>
              <div className="inline-flex rounded border overflow-hidden text-sm" data-testid="analytics-bucket-toggle">
                {BUCKETS.map(b => (
                  <button
                    key={b.key}
                    onClick={() => setBucket(b.key)}
                    data-testid={`analytics-bucket-${b.key}`}
                    className={`px-3 py-1.5 ${
                      bucket === b.key ? 'bg-orange-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    {b.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          <button
            type="button"
            onClick={subTab === 'trends' ? fetchTrends : fetchOta}
            className="text-sm px-3 py-1.5 rounded bg-orange-600 text-white hover:bg-orange-700"
            data-testid="analytics-refresh-btn"
          >
            {loading ? 'Loading…' : 'Refresh'}
          </button>
        </div>
      )}

      {error && (
        <div className="p-3 border-l-4 border-red-500 bg-red-50 text-sm text-red-700" data-testid="analytics-error">
          {error}
        </div>
      )}

      {/* Trends */}
      {subTab === 'trends' && (
        <TrendsPanel loading={loading} data={trends} />
      )}

      {/* OTA Performance */}
      {subTab === 'ota' && (
        <OtaPerformancePanel loading={loading} data={ota} />
      )}

      {/* Integration Health */}
      {subTab === 'health' && (
        <IntegrationHealthPanel loading={loading} data={health} onRefresh={fetchHealth} />
      )}

      {/* Activity Logs */}
      {subTab === 'activity' && (
        <ActivityLogsPanel
          loading={loading}
          data={logs}
          kind={logsKind}
          onKindChange={setLogsKind}
          onRefresh={fetchLogs}
        />
      )}
    </div>
  );
}

/* ----------------------- Trends ----------------------- */

function TrendsPanel({ loading, data }) {
  const series = data?.series || [];
  const maxBookings = Math.max(1, ...series.map(s => s.bookings || 0));
  const totals = series.reduce((acc, s) => {
    acc.bookings      += s.bookings || 0;
    acc.cancellations += s.cancellations || 0;
    return acc;
  }, { bookings: 0, cancellations: 0 });
  const cancelRate = totals.bookings
    ? (totals.cancellations / totals.bookings) * 100 : 0;

  return (
    <div className="space-y-4" data-testid="analytics-trends-panel">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard testid="trends-total-bookings"     label="Bookings"      value={totals.bookings} />
        <KpiCard testid="trends-total-cancellations" label="Cancellations" value={totals.cancellations} />
        <KpiCard testid="trends-cancel-rate"        label="Cancel Rate"   value={percent(cancelRate)} />
        <KpiCard testid="trends-buckets"            label="Buckets"       value={series.length} />
      </div>

      <div className="border rounded-lg bg-white overflow-hidden" data-testid="analytics-trends-chart">
        <div className="px-3 py-2 border-b text-sm font-semibold text-gray-800">Bookings vs Cancellations</div>
        {loading && series.length === 0 ? (
          <div className="p-6 text-sm text-gray-500">Loading…</div>
        ) : series.length === 0 ? (
          <div className="p-6 text-sm text-gray-500" data-testid="analytics-trends-empty">
            No data in this window.
          </div>
        ) : (
          <div className="p-3 space-y-2">
            {series.map(row => {
              const bw = Math.round(((row.bookings || 0) / maxBookings) * 100);
              const cw = Math.round(((row.cancellations || 0) / maxBookings) * 100);
              return (
                <div key={row.bucket} className="text-xs" data-testid={`trends-row-${row.bucket}`}>
                  <div className="flex justify-between text-gray-500 mb-0.5">
                    <span className="font-mono">{row.bucket}</span>
                    <span>
                      <span className="text-emerald-600 font-medium">{row.bookings}</span>
                      {' / '}
                      <span className="text-red-600 font-medium">{row.cancellations}</span>
                    </span>
                  </div>
                  <div className="relative h-3 bg-gray-100 rounded overflow-hidden">
                    <div
                      className="absolute top-0 left-0 h-full bg-emerald-500/80"
                      style={{ width: `${bw}%` }}
                      title={`Bookings: ${row.bookings}`}
                    />
                    <div
                      className="absolute top-0 left-0 h-full bg-red-500/70"
                      style={{ width: `${cw}%` }}
                      title={`Cancellations: ${row.cancellations}`}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

/* ----------------------- OTA Performance ----------------------- */

function OtaPerformancePanel({ loading, data }) {
  const rows = data?.results || [];
  return (
    <div className="border rounded-lg bg-white overflow-hidden" data-testid="analytics-ota-panel">
      <div className="px-3 py-2 border-b text-sm font-semibold text-gray-800">
        OTA Performance · {rows.length} channels
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-xs uppercase text-gray-500">
            <tr>
              <th className="px-3 py-2 text-left">OTA</th>
              <th className="px-3 py-2 text-right">Bookings</th>
              <th className="px-3 py-2 text-right">Room Nights</th>
              <th className="px-3 py-2 text-right">Gross Revenue</th>
              <th className="px-3 py-2 text-right">Share %</th>
              <th className="px-3 py-2 text-right">ADR</th>
              <th className="px-3 py-2 text-right">Cancel %</th>
              <th className="px-3 py-2 text-right">Cancel ₹</th>
            </tr>
          </thead>
          <tbody>
            {loading && rows.length === 0 && (
              <tr><td className="px-3 py-4 text-center text-gray-500" colSpan={8}>Loading…</td></tr>
            )}
            {!loading && rows.length === 0 && (
              <tr>
                <td className="px-3 py-4 text-center text-gray-500" colSpan={8} data-testid="analytics-ota-empty">
                  No OTA activity in this window.
                </td>
              </tr>
            )}
            {rows.map(r => (
              <tr key={r.ota} className="border-t" data-testid={`ota-row-${r.ota}`}>
                <td className="px-3 py-2 font-semibold uppercase">{r.ota}</td>
                <td className="px-3 py-2 text-right">{r.bookings}</td>
                <td className="px-3 py-2 text-right">{r.room_nights}</td>
                <td className="px-3 py-2 text-right">{currency(r.gross_revenue)}</td>
                <td className="px-3 py-2 text-right">{percent(r.share_pct)}</td>
                <td className="px-3 py-2 text-right">{currency(r.adr)}</td>
                <td className="px-3 py-2 text-right">{percent(r.cancellation_rate)}</td>
                <td className="px-3 py-2 text-right">{currency(r.cancellation_amount)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ----------------------- Integration Health ----------------------- */

function IntegrationHealthPanel({ loading, data, onRefresh }) {
  const rows = data?.results || [];
  return (
    <div className="border rounded-lg bg-white overflow-hidden" data-testid="analytics-health-panel">
      <div className="px-3 py-2 border-b flex items-center justify-between">
        <div className="text-sm font-semibold text-gray-800">Integration Health · {rows.length} mappings</div>
        <button
          type="button"
          onClick={onRefresh}
          className="text-xs px-2 py-1 rounded bg-gray-100 hover:bg-gray-200"
          data-testid="analytics-health-refresh"
        >
          {loading ? 'Loading…' : 'Refresh'}
        </button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-xs uppercase text-gray-500">
            <tr>
              <th className="px-3 py-2 text-left">GRB ID</th>
              <th className="px-3 py-2 text-left">Property</th>
              <th className="px-3 py-2 text-left">OTA</th>
              <th className="px-3 py-2 text-left">Hotel Code</th>
              <th className="px-3 py-2 text-left">Last Status</th>
              <th className="px-3 py-2 text-left">Last Error</th>
              <th className="px-3 py-2 text-left">Last At</th>
            </tr>
          </thead>
          <tbody>
            {loading && rows.length === 0 && (
              <tr><td className="px-3 py-4 text-center text-gray-500" colSpan={7}>Loading…</td></tr>
            )}
            {!loading && rows.length === 0 && (
              <tr>
                <td className="px-3 py-4 text-center text-gray-500" colSpan={7} data-testid="analytics-health-empty">
                  No OTA mappings configured for this scope.
                </td>
              </tr>
            )}
            {rows.map((r, idx) => (
              <tr key={`${r.property_id}-${r.ota}-${idx}`} className="border-t" data-testid={`health-row-${idx}`}>
                <td className="px-3 py-2 font-mono text-xs">{r.grb_id || '—'}</td>
                <td className="px-3 py-2">{r.property_name || '—'}</td>
                <td className="px-3 py-2 font-semibold uppercase">{r.ota}</td>
                <td className="px-3 py-2 font-mono text-xs">{r.ota_property_code || '—'}</td>
                <td className="px-3 py-2">
                  {r.last_status ? (
                    <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                      r.last_status === 'success' ? 'bg-emerald-100 text-emerald-700' :
                      r.last_status === 'error' || r.last_status === 'failed' ? 'bg-red-100 text-red-700' :
                      'bg-gray-100 text-gray-700'
                    }`}>{r.last_status}</span>
                  ) : (
                    <span className="text-gray-400 text-xs">no sync yet</span>
                  )}
                </td>
                <td className="px-3 py-2 text-xs text-red-600 max-w-xs truncate" title={r.last_error || ''}>
                  {r.last_error || '—'}
                </td>
                <td className="px-3 py-2 text-[11px] text-gray-500 whitespace-nowrap">
                  {r.last_at ? new Date(r.last_at).toLocaleString() : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ----------------------- Activity Logs ----------------------- */

function ActivityLogsPanel({ loading, data, kind, onKindChange, onRefresh }) {
  const rows = data?.results || [];
  return (
    <div className="space-y-3" data-testid="analytics-activity-panel">
      <div className="flex items-center gap-3">
        <div className="inline-flex rounded border overflow-hidden text-sm" data-testid="activity-kind-toggle">
          <button
            onClick={() => onKindChange('user')}
            data-testid="activity-kind-user"
            className={`px-3 py-1.5 ${kind === 'user' ? 'bg-orange-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
          >
            User Activity
          </button>
          <button
            onClick={() => onKindChange('integration')}
            data-testid="activity-kind-integration"
            className={`px-3 py-1.5 ${kind === 'integration' ? 'bg-orange-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
          >
            Integration Logs
          </button>
        </div>
        <button
          type="button"
          onClick={onRefresh}
          className="text-xs px-2 py-1 rounded bg-gray-100 hover:bg-gray-200"
          data-testid="activity-refresh"
        >
          {loading ? 'Loading…' : 'Refresh'}
        </button>
        <div className="text-xs text-gray-500 ml-auto">{rows.length} entries</div>
      </div>

      <div className="border rounded-lg bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            {kind === 'user' ? (
              <>
                <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                  <tr>
                    <th className="px-3 py-2 text-left">Time</th>
                    <th className="px-3 py-2 text-left">User</th>
                    <th className="px-3 py-2 text-left">Action</th>
                    <th className="px-3 py-2 text-left">Entity</th>
                    <th className="px-3 py-2 text-left">Detail</th>
                  </tr>
                </thead>
                <tbody>
                  {loading && rows.length === 0 && (
                    <tr><td className="px-3 py-4 text-center text-gray-500" colSpan={5}>Loading…</td></tr>
                  )}
                  {!loading && rows.length === 0 && (
                    <tr><td className="px-3 py-4 text-center text-gray-500" colSpan={5} data-testid="activity-user-empty">
                      No user activity recorded.
                    </td></tr>
                  )}
                  {rows.map((r, i) => (
                    <tr key={i} className="border-t" data-testid={`activity-user-row-${i}`}>
                      <td className="px-3 py-2 text-[11px] text-gray-500 whitespace-nowrap">
                        {r.timestamp ? new Date(r.timestamp).toLocaleString() : '—'}
                      </td>
                      <td className="px-3 py-2">{r.user_email || r.user_id || '—'}</td>
                      <td className="px-3 py-2 font-medium">{r.action || '—'}</td>
                      <td className="px-3 py-2">{r.entity_type || '—'}</td>
                      <td className="px-3 py-2 text-xs text-gray-600 max-w-md truncate" title={JSON.stringify(r.details || {})}>
                        {r.entity_id || '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </>
            ) : (
              <>
                <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                  <tr>
                    <th className="px-3 py-2 text-left">Time</th>
                    <th className="px-3 py-2 text-left">Source</th>
                    <th className="px-3 py-2 text-left">Endpoint / Event</th>
                    <th className="px-3 py-2 text-left">Status</th>
                    <th className="px-3 py-2 text-left">Detail</th>
                  </tr>
                </thead>
                <tbody>
                  {loading && rows.length === 0 && (
                    <tr><td className="px-3 py-4 text-center text-gray-500" colSpan={5}>Loading…</td></tr>
                  )}
                  {!loading && rows.length === 0 && (
                    <tr><td className="px-3 py-4 text-center text-gray-500" colSpan={5} data-testid="activity-integration-empty">
                      No integration activity yet.
                    </td></tr>
                  )}
                  {rows.map((r, i) => (
                    <tr key={i} className="border-t" data-testid={`activity-integration-row-${i}`}>
                      <td className="px-3 py-2 text-[11px] text-gray-500 whitespace-nowrap">
                        {r.at ? new Date(r.at).toLocaleString() : '—'}
                      </td>
                      <td className="px-3 py-2 text-xs">
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-700">
                          {r.source || '—'}
                        </span>
                      </td>
                      <td className="px-3 py-2 font-mono text-xs">{r.endpoint || r.event_type || r.stage || '—'}</td>
                      <td className="px-3 py-2">
                        {r.status ? (
                          <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                            r.status === 'success' ? 'bg-emerald-100 text-emerald-700' :
                            'bg-red-100 text-red-700'
                          }`}>{r.status}</span>
                        ) : (
                          <span className="text-gray-400 text-xs">—</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-xs text-red-600 max-w-md truncate"
                          title={r.error || r.booking_id || r.trace_id || ''}>
                        {r.error || r.booking_id || r.trace_id || '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </>
            )}
          </table>
        </div>
      </div>
    </div>
  );
}

/* ----------------------- Small shared bits ----------------------- */

function KpiCard({ label, value, testid }) {
  return (
    <div className="p-3 border rounded-lg bg-white" data-testid={testid}>
      <div className="text-[11px] uppercase tracking-wide text-gray-500">{label}</div>
      <div className="text-xl font-semibold text-gray-800 mt-1">{value}</div>
    </div>
  );
}
